import React, { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Button, Col, Row } from "react-bootstrap";
import { GridColumn } from "@progress/kendo-react-grid";
import { DetailsModal } from "../../../components/grid/DetailsModal";
import { MoneyCell } from "../../../components/grid/Kendo/CustomKendoGridCells";
import {
  DefaultBooleanOrShortWidthColumnWidth,
  DefaultColumnWidth,
  DefaultDateColumnWidth,
  DefaultWideColumnWidth,
} from "../../../components/grid/Kendo/KendoGridAndColumnConstants";
import SharedMainSelectableClientGrid from "shared/components/grids/clientGrid/SharedMainSelectableClientGrid";
import { FormatToCurrency } from "shared/helpers/MoneyHelpers";
import { RxLookupApiEndpoints } from "./RxLookupConstants";
import { useMatchRxLookupClaim } from "./hooks/useMatchRxLookupClaim";
import { useUnmatchRxLookupClaim } from "./hooks/useUnmatchRxLookupClaim";

const ARCHIVE_REQUIRED_MESSAGE =
  "This claim cannot be matched until the payment has been archived.";

const getClaimsFromResponse = (response) => {
  const claims = (response && response.condensedClaims) || [];
  console.log("RxLookupMatchModal - Claims grid data:", claims);
  return claims;
};
const getPaymentsFromResponse = (response) =>
  ((response && response.payments) || []).map((p) => ({
    ...p,
    reconArchive: !!(response && response.reconArchive),
  }));

function formatDate(value) {
  return value ? String(value).slice(0, 10) : "";
}

function getErrorMessage(error) {
  const data = error && error.response ? error.response.data : null;
  if (error && error.response && error.response.status === 422 && data) {
    try {
      const parsed = typeof data === "string" ? JSON.parse(data) : data;
      if (Array.isArray(parsed) && parsed.length > 0) return parsed[0];
    } catch (parseError) {
      // fall through to the generic message
    }
  }
  return "An error has occurred";
}

export default function RxLookupMatchModal(props) {
  const { claim, onClose } = props;

  const [claimKeys, setClaimKeys] = useState(
    claim.clid ? [String(claim.clid)] : []
  );
  const [paymentKeys, setPaymentKeys] = useState([]);
  const [claims, setClaims] = useState([]);
  const [payments, setPayments] = useState([]);
  const [message, setMessage] = useState(null);

  const modalTopRef = useRef(null);
  const scrollTimeoutRef = useRef(null);
  useEffect(() => () => clearTimeout(scrollTimeoutRef.current), []);

  // Wait 1 second after a match/unmatch finishes, then bring the result message into view.
  function scrollToTopAfterDelay() {
    clearTimeout(scrollTimeoutRef.current);
    scrollTimeoutRef.current = setTimeout(() => {
      if (modalTopRef.current) {
        modalTopRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 1000);
  }

  const claimsGridRef = useRef(null);
  const paymentsGridRef = useRef(null);

  // Both grids load from the same endpoint; the extra "grid" param keeps their
  // React Query cache keys distinct so each caches its own slice of the response.
  const claimsApiParams = {
    mmid: claim.mmid,
    rxNumber: claim.rxNumber,
    grid: "claims",
  };
  const paymentsApiParams = {
    mmid: claim.mmid,
    rxNumber: claim.rxNumber,
    grid: "payments",
  };

  const matchMutation = useMatchRxLookupClaim();
  const unmatchMutation = useUnmatchRxLookupClaim();
  const isBusy = matchMutation.isPending || unmatchMutation.isPending;

  const onClaimSelectionChanged = useCallback((keys) => {
    setClaimKeys(keys);
    if (keys.length > 0) setMessage(null);
  }, []);
  const onPaymentSelectionChanged = useCallback((keys) => {
    setPaymentKeys(keys);
    if (keys.length > 0) setMessage(null);
  }, []);

  const selectedClaim = claims.find((c) => String(c.clid) === claimKeys[0]);
  const selectedClid = selectedClaim ? selectedClaim.clid : claimKeys[0] || "";
  const selectedPayments = payments.filter((p) =>
    paymentKeys.includes(String(p.pyid))
  );
  const selectedPyids = selectedPayments.map((p) => p.pyid);
  const reconArchive = payments.length > 0 && payments[0].reconArchive;
  const blockedSelection = selectedPayments.some((p) => !p.canMatch);
  const canMatch =
    !isBusy && selectedClid && selectedPayments.length > 0 && !blockedSelection;
  const canUnmatchSelectedPayments =
    selectedPayments.length > 0 &&
    selectedPayments.every((p) => p.claimId || p.claimId2);
  // With no payments selected, a selected claim that has checks can be unmatched as a whole.
  const canUnmatchSelectedClaim =
    selectedPayments.length === 0 &&
    !!selectedClaim &&
    !!selectedClaim.associatedChecks;
  const canUnmatch =
    !isBusy && (canUnmatchSelectedPayments || canUnmatchSelectedClaim);

  function runMutation(mutation, successText) {
    setMessage(null);
    mutation.mutate(
      {
        mmid: claim.mmid,
        rxNumber: claim.rxNumber,
        clid: selectedClid,
        pyids: selectedPyids,
      },
      {
        onSuccess: () => {
          paymentsGridRef.current.UnselectAllRows();
          setMessage({ variant: "success", text: successText });
          claimsGridRef.current.RefetchGridData();
          paymentsGridRef.current.RefetchGridData();
          scrollToTopAfterDelay();
        },
        onError: (e) => {
          setMessage({ variant: "danger", text: getErrorMessage(e) });
          scrollToTopAfterDelay();
        },
      }
    );
  }

  function matchStatus(payment) {
    if (!payment.claimId && !payment.claimId2) return "Unmatched";
    const matchedClid = payment.claimId || payment.claimId2;
    return matchedClid === selectedClid
      ? "Matched to selected claim"
      : "Matched to claim " + matchedClid;
  }

  const PaymentStatusCell = (cellProps) => (
    <td {...cellProps.tdProps}>{matchStatus(cellProps.dataItem)}</td>
  );

  return (
    <DetailsModal title="Match/Unmatch Claim" show={true} handleClose={onClose}>
      <div ref={modalTopRef} />
      <div className="details-modal-row modal-label">
        <strong>Rx Number:</strong> {claim.rxNumber} &nbsp;
        <strong>NCPDP:</strong> {claim.ncpdp} &nbsp;
        <strong>Dispense Date:</strong> {formatDate(claim.dispenseDate)} &nbsp;
        <strong>Payer:</strong> {claim.payerName} &nbsp;
        <strong>Promise To Pay:</strong> {FormatToCurrency(claim.promiseToPay)}
      </div>

      {message && (
        <Alert variant={message.variant} dismissible onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}
      <h4>Claims</h4>
      <p>
        Choose the claim to match to. Partial fills and rebills of this Rx at
        this location are listed together.
      </p>
      <Row>
        <Col md={12} className="tight-grid multi-line-filter">
          <SharedMainSelectableClientGrid
            ref={claimsGridRef}
            apiGetEndpoint={RxLookupApiEndpoints.GetMatchingData}
            apiParameters={claimsApiParams}
            onDataRetrievedProcessor={getClaimsFromResponse}
            onGridSourceDataChangedCallback={setClaims}
            dataItemKey="clid"
            isSelectable={true}
            singleSelect={true}
            initialSelectedKeys={claim.clid ? [String(claim.clid)] : []}
            onSelectionChangedCallback={onClaimSelectionChanged}
            noGridExportOption={true}
            styleOverride={{ height: "500px" }}
            useQueryOptions={{ refetchOnWindowFocus: false }}
          >
            <GridColumn
              field="serviceDate"
              title="Service Date"
              filter="date"
              format="{0:d}"
              width={DefaultDateColumnWidth()}
            />
            <GridColumn
              field="transactionDate"
              title="Transaction Date"
              filter="date"
              format="{0:d}"
              width={DefaultDateColumnWidth()}
            />
            <GridColumn
              field="payer"
              title="Payer"
              width={DefaultWideColumnWidth()}
            />
            <GridColumn
              field="bin"
              title="BIN"
              width={DefaultBooleanOrShortWidthColumnWidth()}
            />
            <GridColumn
              field="adjudicatedAmount"
              title="Promise To Pay"
              filter="numeric"
              cells={{ data: MoneyCell }}
              width={DefaultColumnWidth()}
            />
            <GridColumn
              field="paidAmount"
              title="Paid"
              filter="numeric"
              cells={{ data: MoneyCell }}
              width={DefaultColumnWidth()}
            />
            <GridColumn
              field="associatedChecks"
              title="Associated Checks"
              width={DefaultColumnWidth()}
            />
            <GridColumn
              field="adjustedBy"
              title="Adjusted By"
              width={DefaultColumnWidth()}
            />
          </SharedMainSelectableClientGrid>
        </Col>
      </Row>

      <h4>Payments</h4>
      {reconArchive && (
        <p>Only archived payments can be matched at this location.</p>
      )}
      <Row>
        <Col md={12} className="tight-grid multi-line-filter">
          <SharedMainSelectableClientGrid
            ref={paymentsGridRef}
            apiGetEndpoint={RxLookupApiEndpoints.GetMatchingData}
            apiParameters={paymentsApiParams}
            onDataRetrievedProcessor={getPaymentsFromResponse}
            onGridSourceDataChangedCallback={setPayments}
            dataItemKey="pyid"
            isSelectable={true}
            onSelectionChangedCallback={onPaymentSelectionChanged}
            noGridExportOption={true}
            styleOverride={{ height: "500px" }}
            useQueryOptions={{ refetchOnWindowFocus: false }}
          >
            <GridColumn
              field="serviceDate"
              title="Service Date"
              filter="date"
              format="{0:d}"
              width={DefaultDateColumnWidth()}
            />
            <GridColumn
              field="payer"
              title="Payer"
              width={DefaultWideColumnWidth()}
            />
            <GridColumn
              field="checkNumber"
              title="Check Number"
              width={DefaultColumnWidth()}
            />
            <GridColumn
              field="checkDate"
              title="Check Date"
              filter="date"
              format="{0:d}"
              width={DefaultDateColumnWidth()}
            />
            <GridColumn
              field="amount"
              title="Amount"
              filter="numeric"
              cells={{ data: MoneyCell }}
              width={DefaultBooleanOrShortWidthColumnWidth()}
            />
            <GridColumn
              field="archiveDate"
              title="Archived"
              filter="date"
              format="{0:d}"
              width={DefaultDateColumnWidth()}
            />
            <GridColumn
              field="claimId"
              title="Status"
              filterable={false}
              sortable={false}
              cells={{ data: PaymentStatusCell }}
              width={DefaultWideColumnWidth()}
            />
          </SharedMainSelectableClientGrid>
        </Col>
      </Row>

      {blockedSelection && (
        <Alert variant="warning">{ARCHIVE_REQUIRED_MESSAGE}</Alert>
      )}

      <Button
        disabled={!canMatch}
        onClick={() => runMutation(matchMutation, "Claim matched.")}
        style={{ backgroundColor: "#015cab", color: "white", marginRight: "1em" }}
      >
        Match
      </Button>
      <Button
        disabled={!canUnmatch}
        variant="secondary"
        onClick={() => runMutation(unmatchMutation, "Claim unmatched.")}
      >
        Unmatch
      </Button>
    </DetailsModal>
  );
}
